import { useEffect } from 'react';

import { createBrowserRouter, Navigate, Outlet, useLocation } from 'react-router-dom';

import {
  aboutRoute,
  authRoute,
  cryptoReportRoute,
  homeRoute,
  ideaRoute,
  interestRoute,
  kimchiPremiumRoute,
  kimchiPremiumDayRoute,
  marketRoute,
  myPageRoute,
  notificationRoute,
  notificationSettingsRoute,
  newsRoute,
  noticeRoute,
  portfolioRoute,
  evaluationRoute,
  gradeGuideRoute,
  userProfileRoute,
  policyRoute,
  realEstateRoute,
  communityRoute,
  financialCalendarRoute,
  financialCalendarDayRoute,
  polymarketRoute,
  polymarketPrivateRoute,
  derivativesRoute,
  journalRoute,
  economicIndicatorsRoute,
  institutionsRoutes,
  financeContentRoute,
  financeRoute,
  legacyFinanceRoutes,
  financeShareCardRoute,
  financeRelationsRoute,
  isInsightsShareCardPath,
} from '@/app/routes';
import { LazyLoadError } from '@/app/routes/LazyLoadError';
import { BoardLayout } from '@/layout/BoardLayout';
import MainLayout from '@/layout/MainLayout';
import NewsLayout from '@/layout/NewsLayout';
import PrivateRoute from '@/router/PrivateRoute';
import NotFound from '@/pages/not-found/NotFound';
import { FirebaseAnalyticsTracker } from '@/shared/configs/FirebaseAnalyticsTracker';
import { ConsentRequiredRedirect, HouseRuleRequiredListener } from '@/features/terms';
import CookieConsentBanner from '@/shared/ui/CookieConsentBanner';
import { useEmbedView } from '@/shared/lib/embed/embed-view';

// TRAD-1953 — Radix Dialog/DropdownMenu(DismissableLayer)는 열림 중 body에 pointer-events:none을
// 걸고, 그 해제는 모듈 전역 카운터(originalBodyPointerEvents)에 의존한다. 오버레이가 닫힘
// 애니메이션(exit transition) 도중인 채로 그 오버레이를 포함한 페이지가 라우트 전환으로 통째로
// unmount되면(예: 삭제 확인 다이얼로그 → 확인 → 목록 이동), 이 전역 카운터가 겹침/경합 상태로
// 남아 pointer-events:none이 화면 전체에 영구히 고착되는 경우가 있다(개별 컴포넌트에서 순서를
// 맞춰도 완전히 막지 못함). RootLayout은 모든 라우트의 공통 조상이라 그 자식 트리의 unmount
// cleanup이 전부 끝난 뒤 이 effect가 가장 나중에 실행된다 — 라우트가 바뀔 때마다 방어적으로
// pointer-events 잠금을 초기화한다. 이 앱은 어떤 페이지도 마운트 시점에 모달을 자동으로 열지
// 않으므로 무조건 초기화해도 안전하다.
const usePointerEventsLockGuard = () => {
  const location = useLocation();
  useEffect(() => {
    document.body.style.removeProperty('pointer-events');
  }, [location.pathname, location.search]);
};

export const RootLayout = () => {
  usePointerEventsLockGuard();
  // C28 — 앱 «보기» 웹뷰(?embed=1)에는 쿠키 배너를 띄우지 않는다(동의 전 = 분석·광고 미로드, 종전과 같은 상태).
  const embed = useEmbedView();
  // TRAD-3867 — 공유(캡처) 카드 `/insights/:slug/card` 에도 배너를 띄우지 않는다(캡처 이미지 위에 덮인다).
  const { pathname } = useLocation();
  const hideCookieBanner = embed || isInsightsShareCardPath(pathname);
  return (
    <>
      <FirebaseAnalyticsTracker />
      {/* G7 — 403 TERMS_007(필수 미동의) → 동의 화면(지금 주소를 from 으로). 모든 라우트의 공통 조상이라 여기 한 곳. */}
      <ConsentRequiredRedirect />
      {/* TRAD-3777 — 403 TERMS_008(하우스 룰 미동의 쓰기) → 글쓰기 때와 같은 하우스 룰 다이얼로그. */}
      <HouseRuleRequiredListener />
      <Outlet />
      {/* 🔴 TRAD-1155 — 쿠키 동의 배너는 **모든 라우트의 공통 조상**인 여기에 둔다.
          MainLayout 에 두면 그 밖에 있는 `authRoute`(로그인/회원가입)에서 안 뜬다 —
          법적 고지가 화면에 따라 있다 없다 하면 안 된다. 라우터 컨텍스트 안이라
          개인정보처리방침 `<Link>` 도 여기서만 쓸 수 있다. */}
      {!hideCookieBanner && <CookieConsentBanner />}
    </>
  );
};

export const router = createBrowserRouter(
  [
    {
      element: <RootLayout />,
      errorElement: <LazyLoadError />,
      children: [
        // TRAD-3867 — 공유(캡처) 카드: 머리글·탭·푸터 없이 정사각 쌍 카드만(MainLayout 밖). noindex.
        financeShareCardRoute,
        {
          element: <MainLayout />,
          children: [
            homeRoute,
            aboutRoute,
            interestRoute,
            ...marketRoute,
            kimchiPremiumRoute,
            kimchiPremiumDayRoute,
            ...noticeRoute,
            ...cryptoReportRoute,
            ...derivativesRoute,
            evaluationRoute,
            gradeGuideRoute,
            userProfileRoute,
            ...policyRoute,
            ...polymarketRoute,
            ...realEstateRoute,
            ...communityRoute,
            financialCalendarRoute,
            financialCalendarDayRoute,
            economicIndicatorsRoute,
            // TRAD-3867 — 인사이트(경제 관계 그림 + 금융 글). 공개 화면 · STATIC_ROUTES 에 등록(프리렌더·sitemap).
            financeRoute,
            financeRelationsRoute,
            financeContentRoute,
            // 10/30 `/finance` → `/insights`(dev 에만 있던 주소 — 리다이렉트만).
            ...legacyFinanceRoutes,
            // TRAD-3582 — 13F. 내비 항목·STATIC_ROUTES 는 API 가 붙기 전까지 넣지 않는다(빈 화면 노출 방지).
            ...institutionsRoutes,
            // 🔴 인증 필요 라우트는 MainLayout **밖의 별도 branch**가 아니라 이 안에 중첩한다.
            // MainLayout이 router.tsx에 두 자리(과거: 여기 + PrivateRoute 안)로 존재하면,
            // 그 사이를 넘나드는 네비게이션마다(예: 홈→내 자산) MainLayout 전체가 unmount+
            // remount되고 그 안 Header의 실시간 시세 WebSocket 구독도 같이 끊겼다 재연결된다
            // (실측 3/3 재현, ~3초 — 「내 자산 진입 후 잠깐 끊긴다」 증상의 근본 원인).
            // PrivateRoute는 이제 레이아웃을 그리지 않고 인증 게이트만 한다.
            {
              element: <PrivateRoute />,
              children: [
                myPageRoute,
                notificationRoute,
                notificationSettingsRoute,
                portfolioRoute,
                ...polymarketPrivateRoute,
                ...journalRoute,
              ],
            },
            {
              path: 'board',
              element: <Navigate to="/board/idea" replace />,
            },
            {
              path: 'community',
              element: <Navigate to="/community/real-estate" replace />,
            },
            {
              element: <BoardLayout />,
              children: [...ideaRoute],
            },
            {
              element: <NewsLayout />,
              children: [...newsRoute],
            },
            {
              path: '*',
              element: <NotFound />,
            },
          ],
        },
        {
          children: [...authRoute],
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);
